/**
 * 0-product-gallery-1.js
 * Product gallery with variant-based image filtering.
 *
 * Alt text convention:  "Color | Descriptive alt text"
 * Images whose alt text prefix matches the selected variant's color are shown.
 * Images with no color prefix are treated as "universal" and always shown.
 */

if (!customElements.get('variant-image-gallery')) {
  class VariantImageGallery extends HTMLElement {
    constructor() {
      super();
      this.variantColors = {};
      this.currentColor = null;
      this.allMediaItems = [];
    }

    connectedCallback() {
      this.mainImage = this.querySelector('[data-main-image]');
      this.mainImageWrapper = this.querySelector('[data-main-image-wrapper]');
      this.thumbnails = this.querySelectorAll('[data-thumbnail]');
      this.thumbnailItems = this.querySelectorAll('[data-thumbnail-item]');

      const jsonEl = this.querySelector('[data-variant-colors]');
      if (jsonEl) {
        try {
          this.variantColors = JSON.parse(jsonEl.textContent);
        } catch (e) { console.error('[variant-image-gallery] Failed to parse variant colors JSON:', e); }
      }

      const mediaEl = this.querySelector('[data-media-list]');
      if (mediaEl) {
        try {
          this.allMediaItems = JSON.parse(mediaEl.textContent);
        } catch (e) { console.error('[variant-image-gallery] Failed to parse media list JSON:', e); }
      }

      /* Inject spinner element */
      if (this.mainImageWrapper) {
        this.spinner = document.createElement('div');
        this.spinner.className = 'fresh-gallery__spinner';
        this.mainImageWrapper.appendChild(this.spinner);
      }

      /* Sync thumbnail column height to main image height so overflow scroll works.
         Run after paint (rAF) so the image has rendered its intrinsic dimensions,
         and again on load in case the image wasn't cached yet. */
      requestAnimationFrame(() => this.syncThumbHeight());
      if (this.mainImage) {
        this.mainImage.addEventListener('load', () => this.syncThumbHeight(), { once: true });
      }
      if (window.ResizeObserver && this.mainImageWrapper) {
        const ro = new ResizeObserver(() => this.syncThumbHeight());
        ro.observe(this.mainImageWrapper);
      }

      this.bindThumbnails();
      this.bindVariantChange();
      this.bindSwipe();
      this.setInitialState();
    }

    syncThumbHeight() {
      const thumbsList = this.querySelector('.fresh-gallery__thumbs');
      if (!thumbsList || !this.mainImageWrapper) return;
      const h = this.mainImageWrapper.offsetHeight;
      if (h > 0) thumbsList.style.maxHeight = h + 'px';
    }

    bindThumbnails() {
      this.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          const mediaId = thumb.getAttribute('data-media-id');
          this.setMainImage(mediaId);
          this.setActiveThumbnail(thumb);
        });

        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            thumb.click();
          }
        });
      });
    }

    bindSwipe() {
      if (!this.mainImageWrapper) return;
      let touchStartX = 0;
      let touchStartY = 0;

      this.mainImageWrapper.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].clientX;
        touchStartY = e.changedTouches[0].clientY;
      }, { passive: true });

      this.mainImageWrapper.addEventListener('touchend', (e) => {
        const deltaX = e.changedTouches[0].clientX - touchStartX;
        const deltaY = e.changedTouches[0].clientY - touchStartY;

        /* Ignore if primarily a vertical scroll */
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;

        const visibleItems = Array.from(this.thumbnailItems).filter(
          (item) => item.style.display !== 'none'
        );
        if (visibleItems.length < 2) return;

        const activeThumb = Array.from(this.thumbnails).find((t) => t.classList.contains('is-active'));
        const activeItem = activeThumb ? activeThumb.closest('[data-thumbnail-item]') : null;
        let currentIndex = visibleItems.indexOf(activeItem);
        if (currentIndex === -1) currentIndex = 0;

        const nextIndex = deltaX < 0
          ? (currentIndex + 1) % visibleItems.length          /* swipe left → next */
          : (currentIndex - 1 + visibleItems.length) % visibleItems.length; /* swipe right → prev */

        const nextItem = visibleItems[nextIndex];
        const nextThumb = nextItem.querySelector('[data-thumbnail]');
        const nextMediaId = nextItem.getAttribute('data-media-id');

        if (nextMediaId) this.setMainImage(nextMediaId);
        if (nextThumb) {
          this.setActiveThumbnail(nextThumb);
          nextThumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, { passive: true });
    }

    getVariantIdFromForm() {
      const productInfo = this.closest('product-info');
      if (productInfo) {
        const input = productInfo.querySelector('input[name="id"]');
        if (input) return input.value;
      }
      const form = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (form) return form.value;
      return null;
    }

    getVariantIdFromUrl() {
      return new URLSearchParams(window.location.search).get('variant');
    }

    bindVariantChange() {
      /* Listen for change events on variant selects/radios */
      document.addEventListener('change', (e) => {
        const picker = e.target.closest('variant-selects, variant-radios');
        if (picker) {
          /* Read from the hidden input which updates synchronously */
          requestAnimationFrame(() => {
            this.updateColorFromVariant();
          });
        }
      });

      /* Watch for the hidden variant input changing (Dawn updates this on variant change) */
      const productInfo = this.closest('product-info');
      if (productInfo) {
        const variantInput = productInfo.querySelector('input[name="id"]');
        if (variantInput) {
          const observer = new MutationObserver(() => this.updateColorFromVariant());
          observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
        }
      }

      /* Handle browser back/forward */
      window.addEventListener('popstate', () => this.updateColorFromVariant());
    }

    setInitialState() {
      this.updateColorFromVariant();
    }

    updateColorFromVariant() {
      const variantId = this.getVariantIdFromForm() || this.getVariantIdFromUrl();

      if (variantId && this.variantColors[variantId]) {
        const newColor = this.variantColors[variantId];
        if (newColor !== this.currentColor) {
          this.currentColor = newColor;
          this.filterByColor(newColor);
        }
      } else if (!variantId) {
        const firstKey = Object.keys(this.variantColors)[0];
        if (firstKey) {
          this.currentColor = this.variantColors[firstKey];
          this.filterByColor(this.currentColor);
        }
      }
    }

    filterByColor(color) {
      const colorLower = color.toLowerCase().trim();
      let firstMatchingThumb = null;
      let firstMatchingMediaId = null;
      let firstUniversalThumb = null;
      let firstUniversalMediaId = null;

      this.thumbnailItems.forEach((item) => {
        const itemColor = (item.getAttribute('data-color') || '').toLowerCase().trim();
        const isExact = itemColor === colorLower;
        const isUniversal = itemColor === '';
        const isMatch = isExact || isUniversal;
        item.style.display = isMatch ? '' : 'none';
        item.setAttribute('aria-hidden', isMatch ? 'false' : 'true');

        if (isExact && !firstMatchingThumb) {
          firstMatchingThumb = item.querySelector('[data-thumbnail]');
          firstMatchingMediaId = item.getAttribute('data-media-id');
        }
        if (isUniversal && !firstUniversalThumb) {
          firstUniversalThumb = item.querySelector('[data-thumbnail]');
          firstUniversalMediaId = item.getAttribute('data-media-id');
        }
      });

      const firstVisibleThumb = firstMatchingThumb || firstUniversalThumb;
      const firstVisibleMediaId = firstMatchingMediaId || firstUniversalMediaId;

      if (firstVisibleMediaId) {
        this.setMainImage(firstVisibleMediaId);
      }
      if (firstVisibleThumb) {
        this.setActiveThumbnail(firstVisibleThumb);
      }

      const status = this.querySelector('[data-gallery-status]');
      if (status) {
        status.textContent = 'Showing images for ' + color;
      }
    }

    setMainImage(mediaId) {
      const media = this.allMediaItems.find((m) => String(m.id) === String(mediaId));
      if (!media || !this.mainImage) return;

      /* Fade out current image and show spinner */
      this.mainImage.style.opacity = '0';
      if (this.spinner) this.spinner.classList.add('is-loading');

      /* Preload the new image so the swap is instant */
      const preload = new Image();
      preload.onload = () => {
        this.mainImage.src = media.src;
        this.mainImage.srcset = media.srcset;
        this.mainImage.alt = media.alt;
        this.mainImage.width = media.width;
        this.mainImage.height = media.height;
        if (this.mainImageWrapper) {
          const ratio = media.width && media.height ? (media.height / media.width * 100) : 100;
          this.mainImageWrapper.style.setProperty('--aspect-ratio', ratio + '%');
        }
        if (this.spinner) this.spinner.classList.remove('is-loading');
        this.mainImage.style.opacity = '1';
        this.syncThumbHeight();
      };
      preload.onerror = () => {
        if (this.spinner) this.spinner.classList.remove('is-loading');
        this.mainImage.style.opacity = '1';
      };
      preload.src = media.src;
    }

    setActiveThumbnail(activeThumb) {
      this.thumbnails.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-current', 'false');
      });
      activeThumb.classList.add('is-active');
      activeThumb.setAttribute('aria-current', 'true');
    }
  }

  customElements.define('variant-image-gallery', VariantImageGallery);
}
